import formStyle from '@/styles/modules/form.module.scss';
import { useState } from 'react';
import { useController } from 'react-hook-form';
import type { DeepMap, FieldError, FieldValues, UseControllerProps } from 'react-hook-form/dist/types';
import styles from './FormDate.module.scss';
import FormLabel from '../FormLabel/FormLabel';
import { newDate } from '../../../utils/date';

type Props<T extends FieldValues> = {
  label?: string;
  placeholder?: string;
  required?: boolean;
  autocompleteYear?: string;
  autocompleteMonth?: string;
  showDay?: boolean;
} & UseControllerProps<T>;

function FormDate<T extends FieldValues>({
  name,
  label,
  required = false,
  control,
  rules,
  autocompleteYear = 'off',
  autocompleteMonth = 'off',
  showDay = true,
}: Props<T>) {
  const {
    field,
    fieldState: { error },
  } = useController<T>({ name, control, rules });

  const initialDate = field.value ? newDate(field.value) : null;

  const [yearValue, setYear] = useState<string>(initialDate ? String(initialDate.getFullYear()) : '');
  const [monthValue, setMonth] = useState<string>(initialDate ? String(initialDate.getMonth() + 1) : '');
  const [dayValue, setDay] = useState<string>(initialDate ? String(initialDate.getDate()) : '');

  const composeFieldValue = (year: string, month: string, day: string) => {
    if (!year && !month && !day) {
      return '';
    }
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // numberではmaxLength効かないので入力値を4桁に制限
    const newYear = e.target.value.slice(0, 4);
    setYear(newYear);
    field.onChange(composeFieldValue(newYear, monthValue, dayValue));
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // numberではmaxLength効かないので入力値を2桁に制限
    const newMonth = e.target.value.slice(0, 2);
    setMonth(newMonth);
    field.onChange(composeFieldValue(yearValue, newMonth, dayValue));
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // numberではmaxLength効かないので入力値を2桁に制限
    const newDay = e.target.value.slice(0, 2);
    setDay(newDay);
    field.onChange(composeFieldValue(yearValue, monthValue, newDay));
  };

  return (
    <div className={`${formStyle.formComponentStyle}`}>
      {/* ラベル */}
      {label && <FormLabel>{label}</FormLabel>}
      {/* フィールドブロック */}
      <div className={error && formStyle.hasError}>
        <div className={`${formStyle.fieldWrapper}`}>
          <input
            type='number'
            value={yearValue}
            onChange={handleYearChange}
            onBlur={field.onBlur}
            autoComplete={autocompleteYear}
            className={styles.input}
          />
          <span className={styles.inputSpan}>年</span>
          <input
            type='number'
            value={monthValue}
            onChange={handleMonthChange}
            onBlur={field.onBlur}
            autoComplete={autocompleteMonth}
            className={styles.input}
          />
          <span className={styles.inputSpan}>月</span>
          {showDay && (
            <>
              <input
                type='number'
                value={dayValue}
                onChange={handleDayChange}
                onBlur={field.onBlur}
                className={styles.input}
              />
              <span>日</span>
            </>
          )}
        </div>
        {/* エラー */}
        {error && <p className={formStyle.validateErrors}>{(error as DeepMap<FieldValues, FieldError>).message}</p>}
      </div>
      {/* 隠しinput */}
      <input
        type='hidden'
        required={required}
        name={field.name}
        ref={field.ref}
        onBlur={field.onBlur}
        onChange={field.onChange}
        value={field.value}
      />
    </div>
  );
}

export default FormDate;
